import { useEffect, useState } from 'react'
import api from '@/lib/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PageHeader } from '@/components/dashboard-ui'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'
import { Plus, Trash2, Search, ShieldOff } from 'lucide-react'

interface DemoDncRow {
  id: string
  phoneNumberE164: string
  source: 'manual' | 'callee_request' | 'form'
  notes: string | null
  demoLeadId: string | null
  createdAt: string
}

const SOURCE_BADGE: Record<DemoDncRow['source'], { label: string; className: string }> = {
  manual:         { label: 'Manual',          className: 'bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-700' },
  callee_request: { label: 'Caller request',  className: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900' },
  form:           { label: 'Form opt-out',    className: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900' },
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatPhone(e164: string): string {
  const m = e164.match(/^\+1(\d{3})(\d{3})(\d{4})$/)
  if (m) return `+1 (${m[1]}) ${m[2]}-${m[3]}`
  return e164
}

export default function DemoDncPage() {
  const { toast } = useToast()
  const [rows, setRows] = useState<DemoDncRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [addOpen, setAddOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [removeTarget, setRemoveTarget] = useState<DemoDncRow | null>(null)
  const [removing, setRemoving] = useState(false)

  const [form, setForm] = useState({ phoneNumber: '', notes: '' })

  const load = async (q = search) => {
    setLoading(true)
    try {
      const res = await api.get('/api/platform/demo-dnc', {
        params: { limit: 200, ...(q ? { search: q } : {}) },
      })
      const data = res.data?.data
      setRows(data?.rows || [])
      setTotal(data?.total || 0)
    } catch (err: any) {
      toast({
        title: 'Failed to load',
        description: err?.response?.data?.message || err.message,
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
    void load(searchInput)
  }

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.phoneNumber.trim()) {
      toast({ title: 'Phone number required', variant: 'destructive' })
      return
    }
    setSubmitting(true)
    try {
      await api.post('/api/platform/demo-dnc', {
        phoneNumber: form.phoneNumber.trim(),
        notes: form.notes.trim() || undefined,
      })
      toast({ title: 'Added to demo DNC', description: form.phoneNumber })
      setForm({ phoneNumber: '', notes: '' })
      setAddOpen(false)
      void load()
    } catch (err: any) {
      toast({
        title: 'Failed to add',
        description: err?.response?.data?.message || err.message,
        variant: 'destructive',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleRemove = async () => {
    if (!removeTarget) return
    setRemoving(true)
    try {
      await api.delete(`/api/platform/demo-dnc/${removeTarget.id}`)
      toast({ title: 'Removed from demo DNC' })
      setRemoveTarget(null)
      void load()
    } catch (err: any) {
      toast({
        title: 'Failed to remove',
        description: err?.response?.data?.message || err.message,
        variant: 'destructive',
      })
    } finally {
      setRemoving(false)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Demo Do-Not-Call"
        subtitle="Phone numbers blocked from receiving demo callbacks via legallady.ai/demo. Numbers are added automatically when a prospect asks not to be called again, or manually here."
        actions={
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add number
          </Button>
        }
      />

      <Card>
        <CardHeader className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldOff className="h-5 w-5 text-muted-foreground" />
                Blocked numbers
              </CardTitle>
              <CardDescription>
                {total === 0 ? 'No numbers blocked yet.' : `${total} number${total === 1 ? '' : 's'} blocked.`}
              </CardDescription>
            </div>
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by digits…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-8 w-64"
                />
              </div>
              <Button type="submit" variant="outline">Search</Button>
              {search && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setSearch('')
                    setSearchInput('')
                    void load('')
                  }}
                >
                  Clear
                </Button>
              )}
            </form>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Phone</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">Loading…</TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    {search ? 'No matches.' : 'No demo opt-outs recorded.'}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => {
                  const badge = SOURCE_BADGE[r.source] ?? SOURCE_BADGE.manual
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono">{formatPhone(r.phoneNumberE164)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={badge.className}>{badge.label}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[480px] truncate text-sm text-muted-foreground" title={r.notes || ''}>
                        {r.notes || '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(r.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setRemoveTarget(r)}
                          aria-label="Remove from DNC"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <form onSubmit={handleAdd}>
            <DialogHeader>
              <DialogTitle>Add to demo DNC</DialogTitle>
              <DialogDescription>
                Block a number from receiving demo callbacks. Use E.164 (e.g. +14155551234) or a US-format number.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone number</Label>
                <Input
                  id="phone"
                  value={form.phoneNumber}
                  onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                  placeholder="+1 (415) 555-1234"
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (optional)</Label>
                <Input
                  id="notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="e.g. emailed james@legallady.ai requesting removal"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Adding…' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Remove confirm */}
      <Dialog open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove from demo DNC?</DialogTitle>
            <DialogDescription>
              {removeTarget && (
                <>
                  <span className="font-mono">{formatPhone(removeTarget.phoneNumberE164)}</span> will
                  be allowed to submit a demo request again. Only do this if the block was a mistake
                  or the prospect explicitly asked to be unblocked.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveTarget(null)} disabled={removing}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRemove} disabled={removing}>
              {removing ? 'Removing…' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
